/* ck-delivery-rally
   Delivery progress bar + threshold celebration sequence.

   Passive: live progress bar(s) — one on the PDP purchase panel, one in the
   cart drawer. Both show cart total vs the free-delivery threshold and stay
   in sync on cart mutations.

   Active (one-shot per crossing): when cart total crosses the threshold, fires
   a full-screen celebration — backdrop dim, parcel rises, confetti bursts in
   Picasso line-stroke style, headline types letter-by-letter, shopping-network
   marquee scrolls, auto-dismisses after ~3s.

   Cart mutation detection via fetch interception on /cart/*.js endpoints.
   Re-reads /cart.js after any mutation to get canonical state. */
(function () {
  'use strict';

  // Guard against double-init. This script is loaded from both
  // main-product.liquid (PDP) and cart-form.liquid (cart drawer). On product
  // pages both tags fire, causing two IIFE instances, two fetch hooks, and
  // two celebration triggers. Bail if a prior instance already registered.
  if (window.__ckDeliveryRally) return;

  var DEFAULT_THRESHOLD_CENTS = 14900;
  var CELEBRATION_DURATION_MS = 3000;
  var CONFETTI_COUNT = 48;
  var HEADLINE_LINES = ['FREE AU', 'DELIVERY', 'APPLIES'];
  var MARQUEE_TEXT = 'FREE AU DELIVERY APPLIES  ·  YOUR ORDER SHIPS COMPLIMENTARY  ·  ';

  var state = {
    threshold: DEFAULT_THRESHOLD_CENTS,
    lastTotal: null,
    lastRatio: null,
    celebrated: false,
    progresses: [],
    parcelAsset: null
  };

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ---------------------------------------------------------------------- */
  /* Progress bar wire-up (one or many instances) */

  function collectProgresses() {
    var wraps = document.querySelectorAll('[data-ck-delivery-threshold]');
    state.progresses = [];
    for (var i = 0; i < wraps.length; i++) {
      var wrap = wraps[i];
      var threshold = parseInt(wrap.getAttribute('data-ck-delivery-threshold'), 10);
      if (isFinite(threshold) && threshold > 0) state.threshold = threshold;
      var parcelAsset = wrap.getAttribute('data-ck-parcel-asset');
      if (parcelAsset && !state.parcelAsset) state.parcelAsset = parcelAsset;
      state.progresses.push({
        root: wrap,
        fill: wrap.querySelector('[data-ck-progress-fill]'),
        label: wrap.querySelector('[data-ck-progress-label]')
      });
    }
    return state.progresses.length;
  }

  function applyFraction(p, ratio, percent) {
    if (p.fill) p.fill.style.width = percent + '%';
    p.root.style.setProperty('--ck-fill-percent', percent + '%');
    p.root.style.setProperty('--ck-fill-fraction', ratio.toFixed(4));
  }

  function updateProgress(cartCents) {
    var ratio = Math.max(0, Math.min(1, cartCents / state.threshold));
    var percent = (ratio * 100).toFixed(1);
    var unlocked = cartCents >= state.threshold;

    for (var i = 0; i < state.progresses.length; i++) {
      var p = state.progresses[i];

      // Drawer DOM is re-rendered on every cart mutation, so a "fresh" element
      // has no prior fraction — if we just set the new value, the CSS
      // transition starts from 0 and the truck resets then slides, causing the
      // hop/skip visible on threshold crossings. Snap fresh elements to the
      // previous known ratio first (no transition), force a reflow, then let
      // the target set animate from that snap. Result: smooth slide from the
      // previously-displayed position to the new one, even across DOM swaps.
      if (!p.root.__ckInit && state.lastRatio != null && state.lastRatio !== ratio) {
        p.root.style.transition = 'none';
        if (p.fill) p.fill.style.transition = 'none';
        var prevPercent = (state.lastRatio * 100).toFixed(1);
        applyFraction(p, state.lastRatio, prevPercent);
        void p.root.offsetHeight;
        p.root.style.transition = '';
        if (p.fill) p.fill.style.transition = '';
      }
      p.root.__ckInit = true;

      applyFraction(p, ratio, percent);

      if (p.label) {
        if (cartCents <= 0) {
          p.label.textContent = 'FREE AU DELIVERY OVER ' + money(state.threshold);
        } else if (unlocked) {
          p.label.textContent = 'FREE AU DELIVERY APPLIES';
        } else {
          p.label.textContent = money(state.threshold - cartCents) + ' TO FREE AU DELIVERY';
        }
      }
      if (unlocked) {
        p.root.classList.add('is-unlocked');
      } else {
        p.root.classList.remove('is-unlocked');
      }
    }

    state.lastRatio = ratio;
  }

  /* ---------------------------------------------------------------------- */
  /* Cart mutation detection via theme events + DOM observer.
     The theme replaces #AjaxCartForm.innerHTML on every cart mutation, which
     destroys the drawer progress bar. We must re-collect nodes AFTER that
     replacement — so we listen to events the theme dispatches post-replace
     (krown:cart:add, krown:cart:update) and observe #AjaxCartForm for any
     path we don't have an event for (removals, clears). */

  function readCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  var pendingChange = null;
  function onCartChange() {
    if (pendingChange) return;
    pendingChange = setTimeout(function () {
      pendingChange = null;
      readCart().then(function (cart) {
        if (!cart) return;
        var total = cart.total_price;
        var prev = state.lastTotal;
        collectProgresses();
        updateProgress(total);
        if (prev != null && prev < state.threshold && total >= state.threshold) {
          triggerCelebration();
        }
        if (prev != null && prev >= state.threshold && total < state.threshold) {
          state.celebrated = false;
        }
        state.lastTotal = total;
      });
    }, 0);
  }

  function installCartHooks() {
    document.addEventListener('krown:cart:add', onCartChange);
    document.addEventListener('krown:cart:update', onCartChange);

    var cartForm = document.getElementById('AjaxCartForm');
    if (cartForm && typeof MutationObserver === 'function') {
      new MutationObserver(onCartChange).observe(cartForm, {
        childList: true,
        subtree: false
      });
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Celebration sequence */

  function buildOverlay() {
    var overlay = document.createElement('div');
    overlay.className = 'ck-delivery-rally';
    overlay.setAttribute('aria-hidden', 'true');

    var backdrop = document.createElement('div');
    backdrop.className = 'ck-delivery-rally__backdrop';
    overlay.appendChild(backdrop);

    var marquee = document.createElement('div');
    marquee.className = 'ck-delivery-rally__marquee';
    var marqueeInner = document.createElement('div');
    marqueeInner.className = 'ck-delivery-rally__marquee-inner';
    marqueeInner.textContent = MARQUEE_TEXT + MARQUEE_TEXT + MARQUEE_TEXT + MARQUEE_TEXT;
    marquee.appendChild(marqueeInner);
    overlay.appendChild(marquee);

    var canvas = document.createElement('canvas');
    canvas.className = 'ck-delivery-rally__confetti';
    overlay.appendChild(canvas);

    var parcel = document.createElement('div');
    parcel.className = 'ck-delivery-rally__parcel';
    if (state.parcelAsset) {
      var img = document.createElement('img');
      img.src = state.parcelAsset;
      img.alt = '';
      parcel.appendChild(img);
    }
    overlay.appendChild(parcel);

    var headline = document.createElement('div');
    headline.className = 'ck-delivery-rally__headline';
    var globalIdx = 0;
    for (var l = 0; l < HEADLINE_LINES.length; l++) {
      var lineEl = document.createElement('div');
      lineEl.className = 'ck-delivery-rally__word';
      var line = HEADLINE_LINES[l];
      for (var c = 0; c < line.length; c++) {
        var ch = line.charAt(c);
        var span = document.createElement('span');
        span.className = 'ck-delivery-rally__letter';
        span.textContent = ch === ' ' ? ' ' : ch;
        span.style.animationDelay = (400 + globalIdx * 45) + 'ms';
        lineEl.appendChild(span);
        globalIdx++;
      }
      headline.appendChild(lineEl);
    }
    overlay.appendChild(headline);

    return { root: overlay, canvas: canvas };
  }

  function fireConfetti(canvas) {
    var ctx = canvas.getContext('2d');
    var w = window.innerWidth;
    var h = window.innerHeight;
    var dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    ctx.scale(dpr, dpr);

    var cx = w / 2;
    var cy = h * 0.55;

    var particles = [];
    for (var i = 0; i < CONFETTI_COUNT; i++) {
      var angle = (Math.random() * Math.PI * 1.4) - Math.PI * 1.2;
      var speed = 280 + Math.random() * 420;
      particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rot: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 14,
        length: 14 + Math.random() * 18,
        curvature: (Math.random() - 0.5) * 0.8,
        alpha: 1,
        gravity: 620 + Math.random() * 160
      });
    }

    var start = performance.now();
    var DURATION_MS = 1400;

    function draw(now) {
      var dt = Math.min(0.05, (now - (draw.last || now)) / 1000);
      draw.last = now;
      var elapsed = now - start;
      ctx.clearRect(0, 0, w, h);

      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.vy += p.gravity * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.rotSpeed * dt;
        p.alpha = Math.max(0, 1 - (elapsed / DURATION_MS));

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.strokeStyle = 'rgba(26, 24, 20, ' + p.alpha.toFixed(3) + ')';
        ctx.lineWidth = 1.8;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(-p.length / 2, 0);
        ctx.quadraticCurveTo(0, p.length * p.curvature, p.length / 2, 0);
        ctx.stroke();
        ctx.restore();
      }

      if (elapsed < DURATION_MS + 200) {
        requestAnimationFrame(draw);
      } else {
        ctx.clearRect(0, 0, w, h);
      }
    }
    requestAnimationFrame(draw);
  }

  function triggerCelebration() {
    if (state.celebrated) return;
    state.celebrated = true;

    var built = buildOverlay();
    document.body.appendChild(built.root);
    built.root.getBoundingClientRect();
    built.root.classList.add('is-active');

    setTimeout(function () { fireConfetti(built.canvas); }, 420);

    setTimeout(function () {
      built.root.classList.remove('is-active');
      built.root.classList.add('is-exiting');
    }, CELEBRATION_DURATION_MS);

    setTimeout(function () {
      if (built.root.parentNode) built.root.parentNode.removeChild(built.root);
    }, CELEBRATION_DURATION_MS + 500);
  }

  /* ---------------------------------------------------------------------- */
  /* Init */

  function init() {
    if (collectProgresses() === 0) return;
    installCartHooks();
    readCart().then(function (cart) {
      if (cart) {
        state.lastTotal = cart.total_price;
        updateProgress(cart.total_price);
      } else {
        updateProgress(0);
      }
    });

    window.__ckDeliveryRally = {
      trigger: triggerCelebration,
      reset: function () { state.celebrated = false; },
      refresh: onCartChange
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
